/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useRouter } from 'next/navigation';

import { useEvals } from '@/hooks/useEvals';

interface RedirectParams {
  page: string;
  view?: string;
  breakdown?: string;
  eval_name?: string;
  eval_label?: string;
  eval_category?: string;
  factName?: string;
  filterFact?: string | null;
  filterFactIDs?: string | null;
  workflowNames?: string | null;
}

const VIEW_MAPPING = {
  All: 'all',
  Performance: 'performance',
  Reliability: 'reliability',
};

export const useSankeyToTracesNavigation = () => {
  const router = useRouter();
  const { state } = useEvals();
  const evalTemplates = state?.evalTemplates;

  const handleTraceClick = (
    redirect?: RedirectParams | string,
    selectedNodeKey?: string,
    workflowNames?: string | null
  ) => {
    let redirectParams: RedirectParams;
    if (typeof redirect === 'string') {
      redirectParams = { page: 'traces', view: 'performance' };
    } else if (!redirect) {
      redirectParams = { page: 'traces', view: 'performance' };
    } else {
      redirectParams = redirect;
    }
    const currentPath = window.location.pathname;
    const currentParams = new URLSearchParams(window.location.search);
    const isComponentsTab = currentPath.includes('/components');

    if (isComponentsTab) {
      const newPath = `${currentPath}/traces`;

      const newUrl = `${newPath}?${currentParams.toString()}`;
      router.push(newUrl);
    } else {
      const pathParts = currentPath.split('/');
      const appName = pathParts[pathParts.length - 1];

      // Clear any existing view parameters
      currentParams.delete('view_by');
      currentParams.delete('view');

      const newPath = `/apps/${appName}/${redirectParams.page}`;

      // Map view from Insights format to Traces format
      if (redirectParams.view) {
        const mappedView =
          VIEW_MAPPING[redirectParams.view as keyof typeof VIEW_MAPPING] ||
          redirectParams.view.toLowerCase();
        currentParams.set('viewBy', mappedView);
      }

      const isQualityViewNode =
        redirectParams.eval_name && redirectParams.eval_label;

      // add breakdown_filter and eval filters to URL
      if (isQualityViewNode) {
        // for quality view use breakdown and eval filter from API
        if (redirectParams.breakdown) {
          currentParams.set('breakdownFilter', redirectParams.breakdown);
        }

        const matchingTemplate = evalTemplates?.find(
          (evalTemplate: any) => evalTemplate.name === redirectParams.eval_name
        ) as any;
        // check if the criteria is a test (for ex: Replay Tests)
        const isTest =
          redirectParams.eval_category?.toLowerCase() === 'test' ||
          matchingTemplate?.category?.includes('test');

        let evalFilter = '';
        if (isTest) {
          // for tests (Replay Tests), keep @test category (for ex: eval=test_booking@test:PASS or eval=test_booking@test:not_evaluated)
          evalFilter = `${redirectParams.eval_name}@test:${redirectParams.eval_label}`;
        } else if (redirectParams.eval_label === 'not_evaluated') {
          // for not_evaluated (Quality evals), omit category so backend maps to include_not_evaluated (for ex: eval=sentiment:not_evaluated)
          evalFilter = `${redirectParams.eval_name}:not_evaluated`;
        } else if (redirectParams.eval_category) {
          // for LLM/Human (Quality evals), use category and map 'human' to 'manual' (for ex: eval=sentiment@llm:positive or eval=sentiment@manual:negative)
          let category = redirectParams.eval_category.toLowerCase();
          if (category === 'human') {
            category = 'manual';
          }
          evalFilter = `${redirectParams.eval_name}@${category}:${redirectParams.eval_label}`;
        } else {
          // append '@<category>' suffix if the criteria is categorized
          const category = matchingTemplate?.category?.[0]?.toLowerCase() || '';
          const categorySuffix = category ? `@${category}` : '';
          evalFilter = `${redirectParams.eval_name}${categorySuffix}:${redirectParams.eval_label}`;
        }

        currentParams.set('eval', evalFilter);
      } else if (selectedNodeKey) {
        currentParams.set('breakdownFilter', selectedNodeKey);
      }
      if (redirectParams.factName) {
        currentParams.set('factName', redirectParams.factName);
      }
      // add workflow filter to URL
      if (workflowNames !== undefined && workflowNames !== null) {
        currentParams.set('workflowNames', workflowNames);
      }
      //add filter_fact
      if (
        redirectParams.filterFact !== undefined &&
        redirectParams.filterFact !== null
      ) {
        currentParams.set('filterFact', redirectParams.filterFact);
      }
      //add filter_fact_ids
      if (
        redirectParams.filterFactIDs !== undefined &&
        redirectParams.filterFactIDs !== null
      ) {
        currentParams.set('filterFactIDs', redirectParams.filterFactIDs);
      }

      const newUrl = `${newPath}?${currentParams.toString()}`;
      router.push(newUrl);
    }
  };

  return { handleTraceClick };
};
